import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../axiosInstance";

const fmt = (v) => '₹' + Math.round(parseFloat(v) || 0).toLocaleString('en-IN');
const fmtDate = (v) => {
    if (!v) return '--';
    const s = v.toString().substring(0, 10);
    const [y, m, d] = s.split('-');
    return `${d}-${m}-${y}`;
};

const PERIODS = [
    { label: 'Today',      value: 'today'      },
    { label: 'This Week',  value: 'week'       },
    { label: 'This Month', value: 'thisMonth'  },
    { label: 'Last Month', value: 'lastMonth'  },
    { label: 'All Time',   value: 'allTime'    },
];

function periodRange(p) {
    const now = new Date();
    const y = now.getFullYear(), mo = now.getMonth(), d = now.getDate();
    const pad = (n) => String(n).padStart(2, '0');
    const iso = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;

    const todayStr = `${y}-${pad(mo + 1)}-${pad(d)}`;
    if (p === 'today') return { from: todayStr, to: todayStr };
    if (p === 'week') {
        const sun = new Date(now); sun.setDate(d - now.getDay());
        return { from: iso(sun), to: todayStr };
    }
    if (p === 'thisMonth') return { from: `${y}-${pad(mo + 1)}-01`, to: todayStr };
    if (p === 'lastMonth') {
        const lm = new Date(y, mo, 0);
        return { from: `${lm.getFullYear()}-${pad(lm.getMonth() + 1)}-01`, to: iso(lm) };
    }
    return { from: '2000-01-01', to: todayStr };
}

export default function AccountsEmiCollection() {
    const [period, setPeriod]   = useState('thisMonth');
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/receipt/');
            const all = Array.isArray(res.data) ? res.data : [];
            const { from, to } = periodRange(period);
            setRows(all.filter(r => {
                const ds = (r.createdAt || '').toString().substring(0, 10);
                return ds >= from && ds <= to;
            }));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [period]);

    useEffect(() => { load(); }, [load]);

    const total     = rows.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);
    const byMode    = rows.reduce((acc, r) => {
        const m = r.payment_type || 'Unknown';
        acc[m] = (acc[m] || 0) + (parseFloat(r.total) || 0);
        return acc;
    }, {});

    const exportCSV = () => {
        const headers = 'Date,Reg No,Recipient,City,No of Dues,Total Paid,Payment Type';
        const data = rows.map(r => [fmtDate(r.createdAt), r.reg_no, r.recipient_name, r.recipient_city, r.noofdue, r.total, r.payment_type].map(v => `"${v ?? ''}"`).join(','));
        const blob = new Blob([[headers, ...data].join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'EMI-Collection.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">
                                <i className="fas fa-money-bill-wave text-success mr-2"></i>EMI Collection
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/accounts">Accounts</a></li>
                                <li className="breadcrumb-item active">EMI Collection</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    {/* Period selector */}
                    <div className="card mb-3">
                        <div className="card-body py-2">
                            <div className="row align-items-center">
                                <div className="col-md-7">
                                    <div className="btn-group btn-group-sm flex-wrap">
                                        {PERIODS.map(p => (
                                            <button key={p.value} className={`btn ${period === p.value ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setPeriod(p.value)}>
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <span className="badge badge-secondary px-3 py-2">{rows.length} receipts</span>
                                </div>
                                <div className="col-md-2 text-right">
                                    <button className="btn btn-sm btn-outline-success mr-1" onClick={exportCSV} disabled={rows.length === 0}>
                                        <i className="fas fa-file-csv"></i>
                                    </button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={load}>
                                        <i className="fas fa-sync-alt"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary cards */}
                    {!loading && rows.length > 0 && (
                        <div className="row mb-3">
                            <div className="col-md-4 mb-2">
                                <div className="callout callout-success">
                                    <h5>Total Collected</h5>
                                    <p className="h3 font-weight-bold text-success mb-0">{fmt(total)}</p>
                                </div>
                            </div>
                            {Object.entries(byMode).map(([mode, amt]) => (
                                <div key={mode} className="col-md-4 mb-2">
                                    <div className="callout callout-info">
                                        <h5>{mode}</h5>
                                        <p className="h4 font-weight-bold mb-0">{fmt(amt)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Table */}
                    <div className="card">
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>
                            ) : rows.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="fas fa-inbox fa-3x mb-3 d-block"></i>No receipts for this period
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-sm mb-0">
                                        <thead className="thead-light">
                                            <tr><th>#</th><th>Date</th><th>Reg No</th><th>Recipient</th><th>City</th><th>No of Dues</th><th>Total Paid</th><th>Payment</th></tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r, i) => (
                                                <tr key={i}>
                                                    <td>{i + 1}</td>
                                                    <td>{fmtDate(r.createdAt)}</td>
                                                    <td>{r.reg_no || '--'}</td>
                                                    <td>{r.recipient_name || '--'}</td>
                                                    <td>{r.recipient_city || '--'}</td>
                                                    <td>{r.noofdue || '--'}</td>
                                                    <td className="font-weight-bold text-success">{fmt(r.total)}</td>
                                                    <td>{r.payment_type || '--'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
